import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingSpinner } from '../../LoadingSpinner';
import Prerequisites from './Prerequisites';
import { useAccreditationApplications } from '../../../hooks/useAccreditationApplications';
import { IAccreditationApplication } from '../../../interfaces/IAccreditationApplication';
import { LockOwner } from '../../../graphql/__generated__/graphql';
import { Col, Row } from 'react-bootstrap';

interface Props {
    renewal?: boolean;
}

const PrerequisitesPage: React.FC<Props> = ({ renewal = false }) => {
    const navigate = useNavigate();
    const applyOrRenew = renewal ? 'renew' : 'apply';
    const { accreditationTypeId } = useParams<{
        accreditationTypeId: string;
    }>();
    const {
        models: { accreditationTypes, applications, loading },
        operations: { upsertApplication },
    } = useAccreditationApplications();

    const application = useMemo<IAccreditationApplication | undefined>(() => {
        if (loading) return undefined;

        const accreditationType = accreditationTypes.find(
            (type) => type.accreditationTypeId === accreditationTypeId
        );

        if (!accreditationType) {
            return undefined;
        }

        const existingApplication = applications.find(
            (app) => app.accreditationType.accreditationTypeId === accreditationTypeId
        );

        if (existingApplication) {
            return existingApplication;
        }

        return {
            accreditationType,
            lockedBy: LockOwner.Applicant,
            jsonData: '{}',
            hasMitigation: false,
            isRenewal: renewal,
        } as IAccreditationApplication;
    }, [loading, accreditationTypeId, accreditationTypes, renewal, applications]);

    useEffect(() => {
        if (!loading && application?.accreditationApplicationId) {
            navigate(`/accreditation/${applyOrRenew}/${application.accreditationApplicationId}`);
        }
    }, [loading, application, navigate]);

    useEffect(() => {
        if (!loading && !application) {
            navigate('/accreditation');
        }
    }, [loading, application, navigate]);

    if (loading || !application) {
        return <LoadingSpinner />;
    }

    const handleBeginApplication = async () => {
        const result = await upsertApplication(application);
        navigate(`/accreditation/${applyOrRenew}/${result.accreditationApplicationId}`);
    };

    return (
        <>
            <h1 className="fs-1 fw-bold mb-5">
                Apply for {application.accreditationType.name} {application.isRenewal && 're'}
                accreditation
            </h1>
            <Row>
                <Col>
                    <Prerequisites
                        handleBeginApplication={handleBeginApplication}
                        renewal={renewal}
                    />
                </Col>
            </Row>
        </>
    );
};

export default PrerequisitesPage;
